import { HouseIcon, Maximize2Icon, Minimize2Icon, PanelLeftIcon, PlusIcon, XIcon } from 'components/icons';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { AiMark } from 'components/ai/AiMark';
import { RecordChip } from 'components/record-chip/RecordChip';
import { Button } from 'components/ui/button';
import { Tooltip } from 'components/ui/tooltip';
import type { GlobalSearchEntityType } from 'types/globalSearch';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { PageContext, PageObjectType } from './usePageContext';

/** The record each record page shows, as the record chip names its family. */
const PAGE_RECORD_TYPE: Partial<Record<PageObjectType, GlobalSearchEntityType>> = {
    contact: 'PARTY',
    matter: 'MATTER',
    invoice: 'INVOICE',
    effort: 'EFFORT',
    timecard: 'TIMECARD',
    employee: 'EMPLOYEE',
    group: 'PARTY_GROUP',
    team: 'TEAM',
    office: 'OFFICE',
    'practice-area': 'PRACTICE_AREA',
    'job-role': 'JOB_ROLE',
    'job-position': 'JOB_POSITION',
    'experience-record': 'EXPERIENCE_RECORD',
};

interface FloatingAiPanelHeaderProps {
    title: string;
    /** The page the assistant can see; null on pages it knows nothing about, which get no "Looking at" line. */
    pageContext: PageContext | null;
    /** The record's name as its page published it; null on a list, or while the record loads. */
    recordName: string | null;
    isHistoryOpen: boolean;
    /** False on a panel too narrow to show the history column beside the conversation. */
    canToggleHistory: boolean;
    isExpanded: boolean;
    onToggleHistory: () => void;
    onNewChat: () => void;
    onToggleExpanded: () => void;
    onClose: () => void;
    /** The panel is being moved: a button the pointer runs onto opens no tooltip. */
    isMoving?: boolean;
}

function HeaderButton({
    label,
    tooltip = label,
    pressed,
    quiet,
    onClick,
    children,
}: {
    label: string;
    /** What the tooltip says, where it differs from the name a toggle keeps whether pressed or not. */
    tooltip?: string;
    pressed?: boolean;
    /** Opens no tooltip. */
    quiet?: boolean;
    onClick: () => void;
    children: ReactNode;
}) {
    return (
        <Tooltip content={tooltip} disabled={quiet}>
            <Button
                variant="ghost"
                size="icon-sm"
                onClick={onClick}
                aria-label={label}
                aria-pressed={pressed}
                className="text-foreground-tertiary hover:bg-card/80 aria-pressed:bg-card aria-pressed:text-foreground new:[&_svg[stroke]]:stroke-[1.5px]"
            >
                {children}
            </Button>
        </Tooltip>
    );
}

/** What the panel is looking at: the record's chip on a record page, a neutral chip led by the house on any other. */
function LookingAtChip({ pageContext, recordName }: { pageContext: PageContext; recordName: string | null }) {
    const intl = useIntl();
    const typeLabel = intl.formatMessage({ id: `ai-chat.floating.page-type.${pageContext.objectType}` });
    const entityType = PAGE_RECORD_TYPE[pageContext.objectType];
    if (pageContext.id != null && entityType) {
        return (
            <RecordChip
                entityType={entityType}
                id={Number(pageContext.id)}
                label={recordName ?? `${typeLabel} #${pageContext.id}`}
                className="min-w-0"
            />
        );
    }
    return (
        <span
            className={cn('inline-flex min-w-0 items-center gap-1 rounded-md px-1.5 font-medium', NEUTRAL_TILE_CLASS)}
        >
            <HouseIcon className="size-3.5 shrink-0" />
            <span className="truncate" title={typeLabel}>
                {typeLabel}
            </span>
        </span>
    );
}

/** The panel's header on the green wash: the AI mark, the title and its four actions, then what it is looking at. */
const FloatingAiPanelHeader: React.FC<FloatingAiPanelHeaderProps> = ({
    title,
    pageContext,
    recordName,
    isHistoryOpen,
    canToggleHistory,
    isExpanded,
    onToggleHistory,
    onNewChat,
    onToggleExpanded,
    onClose,
    isMoving = false,
}) => {
    const intl = useIntl();
    const resizeTooltip = intl.formatMessage({
        id: isExpanded ? 'ai-chat.floating.shrink' : 'ai-chat.floating.expand',
    });

    return (
        <div className="border-primary-wash-border from-primary-tint to-primary-wash flex shrink-0 flex-col gap-1 border-b bg-radial-[140%_180%_at_0%_0%] ps-4 pe-2 pt-2 pb-2.5">
            <div className="flex items-center gap-2.5">
                <AiMark size="header" />
                <h2 className="text-section text-foreground m-0 min-w-0 flex-1 truncate">{title}</h2>
                {canToggleHistory ? (
                    <HeaderButton
                        quiet={isMoving}
                        label={intl.formatMessage({ id: 'ai-chat.floating.history' })}
                        pressed={isHistoryOpen}
                        onClick={onToggleHistory}
                    >
                        <PanelLeftIcon className="size-4" />
                    </HeaderButton>
                ) : null}
                <HeaderButton
                    label={intl.formatMessage({ id: 'ai-chat.new-chat' })}
                    quiet={isMoving}
                    onClick={onNewChat}
                >
                    <PlusIcon className="size-4" />
                </HeaderButton>
                <HeaderButton
                    quiet={isMoving}
                    label={intl.formatMessage({ id: 'ai-chat.floating.expand' })}
                    tooltip={resizeTooltip}
                    pressed={isExpanded}
                    onClick={onToggleExpanded}
                >
                    <span className="t-icon-swap" data-state={isExpanded ? 'b' : 'a'} aria-hidden="true">
                        <span className="t-icon" data-icon="a">
                            <Maximize2Icon className="size-4" />
                        </span>
                        <span className="t-icon" data-icon="b">
                            <Minimize2Icon className="size-4" />
                        </span>
                    </span>
                </HeaderButton>
                <HeaderButton
                    label={intl.formatMessage({ id: 'ai-chat.floating.close' })}
                    quiet={isMoving}
                    onClick={onClose}
                >
                    <XIcon className="size-4" />
                </HeaderButton>
            </div>
            {pageContext ? (
                <div className="text-caption text-foreground-tertiary flex min-w-0 items-center gap-1.5 ps-[38px] pe-2">
                    <span aria-hidden className="bg-running size-1.5 shrink-0 rounded-full" />
                    <span className="shrink-0">
                        <FormattedMessage id="ai-chat.floating.looking-at" />
                    </span>
                    <LookingAtChip pageContext={pageContext} recordName={recordName} />
                </div>
            ) : null}
        </div>
    );
};

export default FloatingAiPanelHeader;
